import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  alpha,
  Stack,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Check as CheckIcon,
  Star as StarIcon,
  Groups as GroupsIcon,
  Security as SecurityIcon,
  Cloud as CloudIcon,
  Support as SupportIcon,
  Speed as SpeedIcon,
  Business as BusinessIcon,
  ExpandMore as ExpandMoreIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import Grid from '@mui/material/Grid';

const PricingPage = () => {
  const theme = useTheme();
  const [billingPeriod, setBillingPeriod] = useState('monthly');

  const handleBillingChange = (event, newBilling) => {
    if (newBilling !== null) {
      setBillingPeriod(newBilling);
    }
  };

  const plans = [
    {
      name: 'Community',
      tagline: 'Perfect for personal use',
      price: { monthly: 0, yearly: 0 },
      priceLabel: 'Free',
      color: theme.palette.success.main,
      icon: <CodeIcon sx={{ fontSize: 40 }} />,
      features: [
        'Up to 5 users',
        'Non-networked environments',
        'Unlimited password storage',
        'AES-256 encryption',
        'Password generator',
        'Apache 2.0 license',
        'Community support',
        'Desktop application',
      ],
      limitations: [
        'No network sync',
        'No priority support',
        'No custom integrations',
      ],
      buttonText: 'Download Free',
      buttonVariant: 'outlined',
      recommended: false,
    },
    {
      name: 'Business',
      tagline: 'For growing teams',
      price: { monthly: 4, yearly: 3.5 },
      priceLabel: 'per user/month',
      color: theme.palette.primary.main,
      icon: <BusinessIcon sx={{ fontSize: 40 }} />,
      features: [
        '6-25 users',
        'Basic network support',
        'Team password sharing',
        'User management dashboard',
        'Activity logs',
        'Directory integration (LDAP)',
        'Email support',
        '2FA/MFA support',
        'Data export tools',
      ],
      limitations: [
        'Limited to 25 users',
        'Standard support only',
      ],
      buttonText: 'Start Free Trial',
      buttonVariant: 'contained',
      recommended: true,
    },
    {
      name: 'Professional',
      tagline: 'For larger organizations',
      price: { monthly: 6, yearly: 5 },
      priceLabel: 'per user/month',
      color: theme.palette.secondary.main,
      icon: <StarIcon sx={{ fontSize: 40 }} />,
      features: [
        '26-100 users',
        'Advanced network features',
        'Priority support',
        'SSO integration',
        'Advanced policies',
        'API access',
        'Compliance reports',
        'Dedicated account manager',
        'Custom security policies',
        'Audit trails',
      ],
      limitations: [
        'Limited to 100 users',
      ],
      buttonText: 'Contact Sales',
      buttonVariant: 'contained',
      recommended: false,
    },
    {
      name: 'Enterprise',
      tagline: 'For large-scale deployments',
      price: { monthly: 'Custom', yearly: 'Custom' },
      priceLabel: 'Custom pricing',
      color: theme.palette.error.main,
      icon: <GroupsIcon sx={{ fontSize: 40 }} />,
      features: [
        'Unlimited users',
        'Full network deployment',
        'Dedicated support team',
        'Custom features',
        'On-premise deployment',
        'SLA guarantee',
        'Security assessment',
        'Training & onboarding',
        'Custom integrations',
        'White-label options',
      ],
      limitations: [],
      buttonText: 'Get Quote',
      buttonVariant: 'contained',
      recommended: false,
    },
  ];

  const comparisonFeatures = [
    { feature: 'Users', community: 'Up to 5', business: '6-25', professional: '26-100', enterprise: 'Unlimited' },
    { feature: 'Password Storage', community: 'Unlimited', business: 'Unlimited', professional: 'Unlimited', enterprise: 'Unlimited' },
    { feature: 'AES-256 Encryption', community: '✅', business: '✅', professional: '✅', enterprise: '✅' },
    { feature: 'Desktop App', community: '✅', business: '✅', professional: '✅', enterprise: '✅' },
    { feature: 'Password Generator', community: '✅', business: '✅', professional: '✅', enterprise: '✅' },
    { feature: 'Network Sync', community: '❌', business: 'Basic', professional: 'Advanced', enterprise: 'Full' },
    { feature: 'Team Sharing', community: '❌', business: '✅', professional: '✅', enterprise: '✅' },
    { feature: 'User Management', community: '❌', business: '✅', professional: '✅', enterprise: '✅' },
    { feature: 'Activity Logs', community: '❌', business: 'Basic', professional: 'Advanced', enterprise: 'Full' },
    { feature: 'LDAP Integration', community: '❌', business: '✅', professional: '✅', enterprise: '✅' },
    { feature: 'SSO Integration', community: '❌', business: '❌', professional: '✅', enterprise: '✅' },
    { feature: 'API Access', community: '❌', business: '❌', professional: '✅', enterprise: '✅' },
    { feature: 'Priority Support', community: '❌', business: '❌', professional: '✅', enterprise: '✅' },
    { feature: 'Custom Features', community: '❌', business: '❌', professional: 'Limited', enterprise: '✅' },
    { feature: 'SLA Guarantee', community: '❌', business: '❌', professional: '❌', enterprise: '✅' },
    { feature: 'On-Premise Deploy', community: '❌', business: '❌', professional: '❌', enterprise: '✅' },
  ];

  const faqs = [
    {
      question: 'How does PWGen-Rust compare to other password managers?',
      answer: 'PWGen-Rust offers competitive pricing with enterprise features at a fraction of the cost. Our Business plan at $4/user/month competes directly with Bitwarden ($4-6), while offering better performance due to our Rust implementation. We\'re significantly more affordable than Dashlane ($8) or 1Password (custom pricing).',
    },
    {
      question: 'Can I switch plans later?',
      answer: 'Yes! You can upgrade or downgrade your plan at any time. When upgrading, you\'ll be prorated for the remainder of your billing period. Downgrades take effect at the next billing cycle.',
    },
    {
      question: 'Is there a free trial for paid plans?',
      answer: 'Yes, we offer a 30-day free trial for Business and Professional plans. No credit card required. Enterprise customers can request a custom trial period.',
    },
    {
      question: 'What happens to my data if I downgrade?',
      answer: 'Your data remains secure and accessible. If downgrading reduces your user count below your current usage, you\'ll need to remove users or archive their data before the downgrade takes effect.',
    },
    {
      question: 'Do you offer educational or non-profit discounts?',
      answer: 'Yes! Educational institutions and registered non-profits receive 50% off Business and Professional plans. Contact our sales team with proof of status.',
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit cards, ACH transfers, and wire transfers for annual plans. Enterprise customers can request custom billing arrangements.',
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', py: 8 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h2"
            component="h1"
            fontWeight="bold"
            gutterBottom
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2,
            }}
          >
            Simple, Transparent Pricing
          </Typography>
          <Typography variant="h5" color="text.secondary" paragraph>
            Choose the perfect plan for your security needs
          </Typography>
          
          {/* Billing Toggle */}
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
            <ToggleButtonGroup
              value={billingPeriod}
              exclusive
              onChange={handleBillingChange}
              aria-label="billing period"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                borderRadius: 2,
                border: 'none',
                '& .MuiToggleButton-root': {
                  border: 'none',
                  '&:not(:first-of-type)': {
                    borderLeft: 'none',
                  },
                },
              }}
            >
              <ToggleButton value="monthly" sx={{ px: 3 }}>
                Monthly
              </ToggleButton>
              <ToggleButton value="yearly" sx={{ px: 3 }}>
                Yearly
                <Chip
                  label="Save 15%"
                  size="small"
                  color="success"
                  sx={{ ml: 1 }}
                />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* Main Pricing Cards (Community, Business, Professional) */}
        <Grid container spacing={{ xs: 3, md: 4 }} sx={{ mb: 8 }}>
          {plans.filter(plan => plan.name !== 'Enterprise').map((plan) => (
            <Grid size={{ xs: 12, md: 4 }} key={plan.name}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  border: plan.recommended ? `2px solid ${plan.color}` : 'none',
                  transform: plan.recommended ? 'scale(1.05)' : 'scale(1)',
                  transition: 'transform 0.2s',
                  overflow: 'hidden',
                  opacity: plan.name !== 'Community' ? 0.7 : 1,
                  '&:hover': {
                    transform: plan.recommended ? 'scale(1.07)' : 'scale(1.02)',
                  },
                }}
              >
                {plan.recommended && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 20,
                      right: -35,
                      bgcolor: plan.color,
                      color: 'white',
                      px: 6,
                      py: 0.8,
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      transform: 'rotate(45deg)',
                      transformOrigin: 'center',
                      boxShadow: 2,
                      zIndex: 2,
                      minWidth: 100,
                      textAlign: 'center'
                    }}
                  >
                    RECOMMENDED
                  </Box>
                )}
                
                {/* Coming Soon Overlay for non-Community plans */}
                {plan.name !== 'Community' && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%) rotate(-15deg)',
                      backgroundColor: theme.palette.accent?.main || '#00695c',
                      color: 'white',
                      px: 3,
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 700,
                      fontSize: '1.1rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      zIndex: 10,
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
                      border: '2px solid white',
                    }}
                  >
                    Coming Soon
                  </Box>
                )}
                
                <CardContent sx={{ flexGrow: 1, pt: plan.recommended ? 4 : 3 }}>
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Box sx={{ color: plan.color, mb: 2 }}>
                      {plan.icon}
                    </Box>
                    <Typography variant="h4" fontWeight="bold" gutterBottom>
                      {plan.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {plan.tagline}
                    </Typography>
                  </Box>

                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography variant="h3" fontWeight="bold" sx={{ color: plan.color }}>
                      {typeof plan.price === 'object' 
                        ? (plan.price[billingPeriod] === 0 ? 'Free' : 
                           plan.price[billingPeriod] === 'Custom' ? 'Custom' :
                           `$${plan.price[billingPeriod]}`)
                        : plan.price
                      }
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {plan.priceLabel}
                    </Typography>
                    {billingPeriod === 'yearly' && typeof plan.price === 'object' && plan.price.yearly !== 0 && plan.price.yearly !== 'Custom' && (
                      <Typography variant="caption" color="success.main">
                        Save ${((plan.price.monthly - plan.price.yearly) * 12).toFixed(0)}/user/year
                      </Typography>
                    )}
                  </Box>

                  <List dense>
                    {plan.features.map((feature, index) => (
                      <ListItem key={index} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckIcon sx={{ color: plan.color, fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={feature}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>

                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button
                    fullWidth
                    variant={plan.buttonVariant}
                    color={plan.name === 'Community' ? 'success' : 'primary'}
                    size="large"
                    disabled={plan.name !== 'Community'}
                  >
                    {plan.name === 'Community' ? plan.buttonText : 'Coming Soon'}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Enterprise Section */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" fontWeight="bold" textAlign="center" gutterBottom>
            Enterprise Solutions
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center" paragraph>
            Tailored for organizations with complex security requirements
          </Typography>
          
          <Grid container>
            <Grid size={{ xs: 12 }}>
              {plans.filter(plan => plan.name === 'Enterprise').map((plan) => (
                <Card
                  key={plan.name}
                  sx={{
                    background: `linear-gradient(135deg, ${alpha(plan.color, 0.1)} 0%, ${alpha(plan.color, 0.05)} 100%)`,
                    border: `1px solid ${alpha(plan.color, 0.3)}`,
                    position: 'relative',
                    opacity: 0.7,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: theme.shadows[8],
                    },
                    transition: 'all 0.2s',
                  }}
                >
                  {/* Coming Soon Overlay for Enterprise */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%) rotate(-15deg)',
                      backgroundColor: theme.palette.accent?.main || '#00695c',
                      color: 'white',
                      px: 3,
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 700,
                      fontSize: '1.1rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      zIndex: 10,
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
                      border: '2px solid white',
                    }}
                  >
                    Coming Soon
                  </Box>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Box sx={{ color: plan.color, mr: 2 }}>
                        {plan.icon}
                      </Box>
                      <Box>
                        <Typography variant="h4" fontWeight="bold" gutterBottom>
                          {plan.name}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          {plan.tagline}
                        </Typography>
                      </Box>
                      <Box sx={{ ml: 'auto', textAlign: 'center' }}>
                        <Typography variant="h4" fontWeight="bold" sx={{ color: plan.color }}>
                          Custom
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Pricing
                        </Typography>
                      </Box>
                    </Box>

                    <Grid container spacing={{ xs: 3, md: 4 }}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                          Enterprise Features
                        </Typography>
                        <List dense>
                          {plan.features.slice(0, 5).map((feature, index) => (
                            <ListItem key={index} sx={{ px: 0 }}>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <CheckIcon sx={{ color: plan.color, fontSize: 20 }} />
                              </ListItemIcon>
                              <ListItemText 
                                primary={feature}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                          Premium Support
                        </Typography>
                        <List dense>
                          {plan.features.slice(5).map((feature, index) => (
                            <ListItem key={index} sx={{ px: 0 }}>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <CheckIcon sx={{ color: plan.color, fontSize: 20 }} />
                              </ListItemIcon>
                              <ListItemText 
                                primary={feature}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Grid>
                    </Grid>
                  </CardContent>

                  <CardActions sx={{ p: 4, pt: 0 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      size="large"
                      disabled
                      sx={{
                        bgcolor: plan.color,
                        '&:hover': {
                          bgcolor: alpha(plan.color, 0.8),
                        },
                      }}
                    >
                      Coming Soon
                    </Button>
                  </CardActions>
                </Card>
              ))}
            </Grid>
          </Grid>
        </Box>

        {/* Trust Section */}
        <Paper
          sx={{
            p: 4,
            mb: 8,
            textAlign: 'center',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
          }}
        >
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Trusted by Security-Conscious Teams
          </Typography>
          <Grid container spacing={{ xs: 3, md: 4 }} sx={{ mt: 2 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Stack alignItems="center" spacing={1}>
                <SecurityIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />
                <Typography variant="h6">Zero-Knowledge</Typography>
                <Typography variant="body2" color="text.secondary">
                  Your data is encrypted locally before syncing
                </Typography>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Stack alignItems="center" spacing={1}>
                <SpeedIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />
                <Typography variant="h6">Blazing Fast</Typography>
                <Typography variant="body2" color="text.secondary">
                  Built in Rust for maximum performance
                </Typography>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Stack alignItems="center" spacing={1}>
                <CloudIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />
                <Typography variant="h6">Flexible Deployment</Typography>
                <Typography variant="body2" color="text.secondary">
                  Cloud, on-premise, or hybrid options
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {/* Comparison Table */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" fontWeight="bold" textAlign="center" gutterBottom>
            Compare Plans
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center" paragraph>
            All plans include core security features. Choose based on your team size and needs.
          </Typography>
          
          <Box sx={{ mt: 4, overflowX: 'auto' }}>
            <TableContainer 
              component={Paper} 
              sx={{ 
                minWidth: 800,
                border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                borderRadius: 2,
                overflow: 'hidden'
              }}
            >
              <Table sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                    <TableCell 
                      sx={{ 
                        fontWeight: 'bold', 
                        fontSize: '1rem',
                        py: 3,
                        borderRight: `1px solid ${alpha(theme.palette.divider, 0.5)}`
                      }}
                    >
                      Features
                    </TableCell>
                    <TableCell 
                      align="center" 
                      sx={{ 
                        fontWeight: 'bold', 
                        fontSize: '1rem',
                        py: 3,
                        color: theme.palette.success.main,
                        borderRight: `1px solid ${alpha(theme.palette.divider, 0.5)}`
                      }}
                    >
                      Community
                      <Typography variant="caption" display="block" sx={{ opacity: 0.7 }}>
                        Free
                      </Typography>
                    </TableCell>
                    <TableCell 
                      align="center" 
                      sx={{ 
                        fontWeight: 'bold', 
                        fontSize: '1rem',
                        py: 3,
                        color: theme.palette.primary.main,
                        borderRight: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                    >
                      {/* Angled Popular Sash */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 12,
                          right: -30,
                          bgcolor: theme.palette.primary.main,
                          color: 'white',
                          px: 5,
                          py: 0.5,
                          fontSize: '0.7rem',
                          fontWeight: 'bold',
                          transform: 'rotate(45deg)',
                          transformOrigin: 'center',
                          boxShadow: 1,
                          zIndex: 1,
                          minWidth: 80,
                          textAlign: 'center'
                        }}
                      >
                        POPULAR
                      </Box>
                      Business
                      <Typography variant="caption" display="block" sx={{ opacity: 0.7 }}>
                        $4/user/month
                      </Typography>
                    </TableCell>
                    <TableCell 
                      align="center" 
                      sx={{ 
                        fontWeight: 'bold', 
                        fontSize: '1rem',
                        py: 3,
                        color: theme.palette.secondary.main,
                        borderRight: `1px solid ${alpha(theme.palette.divider, 0.5)}`
                      }}
                    >
                      Professional
                      <Typography variant="caption" display="block" sx={{ opacity: 0.7 }}>
                        $6/user/month
                      </Typography>
                    </TableCell>
                    <TableCell 
                      align="center" 
                      sx={{ 
                        fontWeight: 'bold', 
                        fontSize: '1rem',
                        py: 3,
                        color: theme.palette.error.main
                      }}
                    >
                      Enterprise
                      <Typography variant="caption" display="block" sx={{ opacity: 0.7 }}>
                        Custom pricing
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {comparisonFeatures.map((row, index) => (
                    <TableRow 
                      key={row.feature}
                      sx={{ 
                        '&:nth-of-type(odd)': {
                          bgcolor: alpha(theme.palette.primary.main, 0.02)
                        },
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.05)
                        }
                      }}
                    >
                      <TableCell 
                        component="th" 
                        scope="row" 
                        sx={{ 
                          fontWeight: 600,
                          py: 2.5,
                          borderRight: `1px solid ${alpha(theme.palette.divider, 0.5)}`
                        }}
                      >
                        {row.feature}
                      </TableCell>
                      <TableCell 
                        align="center" 
                        sx={{ 
                          py: 2.5,
                          borderRight: `1px solid ${alpha(theme.palette.divider, 0.5)}`
                        }}
                      >
                        {row.community === '✅' ? (
                          <CheckIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />
                        ) : row.community === '❌' ? (
                          <Box sx={{ color: theme.palette.text.disabled, fontSize: '1.2rem' }}>—</Box>
                        ) : (
                          <Typography variant="body2" fontWeight={500}>
                            {row.community}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell 
                        align="center" 
                        sx={{ 
                          py: 2.5,
                          borderRight: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                          bgcolor: index % 2 === 0 
                            ? alpha(theme.palette.primary.main, 0.03) 
                            : alpha(theme.palette.primary.main, 0.05)
                        }}
                      >
                        {row.business === '✅' ? (
                          <CheckIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                        ) : row.business === '❌' ? (
                          <Box sx={{ color: theme.palette.text.disabled, fontSize: '1.2rem' }}>—</Box>
                        ) : (
                          <Typography variant="body2" fontWeight={500}>
                            {row.business}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell 
                        align="center" 
                        sx={{ 
                          py: 2.5,
                          borderRight: `1px solid ${alpha(theme.palette.divider, 0.5)}`
                        }}
                      >
                        {row.professional === '✅' ? (
                          <CheckIcon sx={{ color: theme.palette.secondary.main, fontSize: 20 }} />
                        ) : row.professional === '❌' ? (
                          <Box sx={{ color: theme.palette.text.disabled, fontSize: '1.2rem' }}>—</Box>
                        ) : (
                          <Typography variant="body2" fontWeight={500}>
                            {row.professional}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell 
                        align="center" 
                        sx={{ py: 2.5 }}
                      >
                        {row.enterprise === '✅' ? (
                          <CheckIcon sx={{ color: theme.palette.error.main, fontSize: 20 }} />
                        ) : row.enterprise === '❌' ? (
                          <Box sx={{ color: theme.palette.text.disabled, fontSize: '1.2rem' }}>—</Box>
                        ) : (
                          <Typography variant="body2" fontWeight={500}>
                            {row.enterprise}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Quick Action Buttons */}
          <Grid container spacing={2} sx={{ mt: 4 }} justifyContent="center">
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Button
                fullWidth
                variant="outlined"
                color="success"
                size="large"
                startIcon={<CheckIcon />}
                sx={{ py: 1.5 }}
              >
                Download Community
              </Button>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<StarIcon />}
                sx={{ 
                  py: 1.5,
                  bgcolor: theme.palette.accent?.main || theme.palette.primary.main,
                  color: theme.palette.accent?.contrastText || 'white',
                  '&:hover': {
                    bgcolor: theme.palette.accent?.dark || theme.palette.primary.dark,
                  },
                }}
              >
                Try Business Free
              </Button>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Button
                fullWidth
                variant="outlined"
                color="secondary"
                size="large"
                startIcon={<BusinessIcon />}
                sx={{ py: 1.5 }}
              >
                Contact for Pro
              </Button>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Button
                fullWidth
                variant="outlined"
                color="error"
                size="large"
                startIcon={<GroupsIcon />}
                sx={{ py: 1.5 }}
              >
                Get Enterprise Quote
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Special Offers */}
        <Box sx={{ mb: 8, textAlign: 'center' }}>
          <Grid container spacing={{ xs: 3, md: 4 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <GroupsIcon sx={{ fontSize: 48, color: theme.palette.accent?.main || theme.palette.primary.main, mb: 2 }} />
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Municipal & Non-Profit Discount
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Special pricing for municipalities, hospitals, veterans organizations, emergency services, and qualified non-profits
                </Typography>
                <Button 
                  variant="outlined" 
                  sx={{ 
                    borderColor: theme.palette.accent?.main || theme.palette.primary.main,
                    color: theme.palette.accent?.main || theme.palette.primary.main,
                    '&:hover': {
                      borderColor: theme.palette.accent?.dark || theme.palette.primary.dark,
                      backgroundColor: alpha(theme.palette.accent?.main || theme.palette.primary.main, 0.08),
                    }
                  }}
                  href="/special-pricing"
                >
                  Learn More
                </Button>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <SupportIcon sx={{ fontSize: 48, color: theme.palette.accent?.main || theme.palette.primary.main, mb: 2 }} />
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Migration Assistance
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Free migration support when switching from competitors
                </Typography>
                <Button 
                  variant="outlined" 
                  sx={{ 
                    borderColor: theme.palette.accent?.main || theme.palette.primary.main,
                    color: theme.palette.accent?.main || theme.palette.primary.main,
                    '&:hover': {
                      borderColor: theme.palette.accent?.dark || theme.palette.primary.dark,
                      backgroundColor: alpha(theme.palette.accent?.main || theme.palette.primary.main, 0.08),
                    }
                  }}
                  href="/docs/getting-started/migration"
                >
                  Get Support
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* FAQs */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" fontWeight="bold" textAlign="center" gutterBottom>
            Frequently Asked Questions
          </Typography>
          
          <Box sx={{ mt: 4, maxWidth: 800, mx: 'auto' }}>
            {faqs.map((faq, index) => (
              <Accordion key={index}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">{faq.question}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>{faq.answer}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Box>

      </Container>
    </Box>
  );
};

export default PricingPage;